import { Gid } from "@redotech/shopify-util/id";
import * as z from "zod/v4";

// Branded types to distinguish between percentage (0-100) and decimal (0.0-1.0) values
export type DiscountPercentage = number & {
  readonly __brand: "DiscountPercentage";
};
export type DiscountDecimal = number & { readonly __brand: "DiscountDecimal" };

// Helper function to convert discount percentage (1-100) to decimal (0.0-1.0)
export function percentageToDecimal(
  percentage: DiscountPercentage,
): DiscountDecimal {
  return (percentage / 100) as DiscountDecimal;
}

export enum ExpirationType {
  /** Expires when the associated order is delivered */
  DELIVERY = "delivery",
  /** Expires a certain number of days after the discount is sent to the customer */
  EXPIRATION_DAYS = "expiration",
  /** Expires at a specific date */
  DATE = "date",
  /** Never expires */
  NEVER = "never",
}

export enum DiscountGroupType {
  STATIC = "static",
  DYNAMIC = "dynamic",
}

export enum DiscountType {
  FREE_SHIPPING = "freeShipping",
  ORDER = "order",
  PRODUCT = "product",
  BUY_X_GET_Y = "buyXGetY",
}

export enum DiscountProductType {
  PRODUCT = "product",
  COLLECTION = "collection",
}

export enum DiscountProvider {
  SHOPIFY = "shopifyDiscount",
  COMMENTSOLD = "commentsold",
  OTHER = "other",
}

export interface DiscountCollection {
  id?: Gid;
  name?: string;
}

export enum DiscountCollectionsSelectionType {
  INCLUDE = "include",
  EXCLUDE = "exclude",
}
export interface DiscountProduct {
  id?: string;
  title?: string;
}

export enum DiscountValueType {
  PERCENTAGE = "percentage",
  AMOUNT = "amount",
}

export enum DiscountMinRequirementType {
  MINIMUM_QUANTITY = "minimumQuantity",
  MINIMUM_SUBTOTAL = "minimumSubtotal",
}

export interface DiscountSettingsBasic {
  discountType: DiscountType;
  expirationType?: ExpirationType;
  expirationDays?: number;
  combinesWith?: {
    orderDiscounts?: boolean;
    productDiscounts?: boolean;
    shippingDiscount?: boolean;
  };
  minimumRequirement?: { type: DiscountMinRequirementType; minimum: number };
  defaultText?: string;
}

export interface DiscountSettingsOrder extends DiscountSettingsBasic {
  discountType: DiscountType.ORDER;
  discountValueType: DiscountValueType;
  discountAmountValue?: number;
  discountPercentageValue?: number; // must be a number between 0.0 and 1.0
}

export interface DiscountSettingsFreeShipping extends DiscountSettingsBasic {
  discountType: DiscountType.FREE_SHIPPING;
  freeShippingCountryCodes?: string[];
  maximumShippingPrice: number;
}

export interface DiscountSettingsProduct extends DiscountSettingsBasic {
  discountType: DiscountType.PRODUCT;
  discountProductType: DiscountProductType;
  discountProducts?: DiscountProduct[];
  discountCollections?: DiscountCollection[];
  discountCollectionsSelectionType?: DiscountCollectionsSelectionType;
  discountValueType: DiscountValueType;
  discountAmountValue?: number;
  discountPercentageValue?: number; // must be a number between 0.0 and 1.0
}

export interface DiscountSettingsBuyXGetY extends DiscountSettingsBasic {
  discountType: DiscountType.BUY_X_GET_Y;
  customerBuys: {
    items: { products?: DiscountProduct[]; collections?: DiscountCollection[] };
    quantity: number;
  };
  customerGets: {
    items: { products?: DiscountProduct[]; collections?: DiscountCollection[] };
    quantity: number;
    value: {
      discountValueType: DiscountValueType;
      discountAmountValue?: number;
      discountPercentageValue?: DiscountDecimal; // must be a number between 0.0 and 1.0
    };
  };
}

export type DiscountSettings =
  | DiscountSettingsOrder
  | DiscountSettingsFreeShipping
  | DiscountSettingsProduct
  | DiscountSettingsBuyXGetY;

/**
 * Line item discount schemas.
 * Either percentValue OR fixedValue, not both.
 * Note: Shopify API expects fixedValue as string, but we store as number internally
 * and convert when calling the API.
 */
export const lineItemDiscountPercentSchema = z.object({
  description: z.string(),
  /** Percentage discount (0-100, e.g., 10 = 10% off) */
  percentValue: z.number(),
});

export const lineItemDiscountFixedSchema = z.object({
  description: z.string(),
  /** Fixed amount discount in the order's currency (e.g., 10.00 = 10 units off) */
  fixedValue: z.number(),
});

const lineItemDiscountInputSchema_ = z.union([
  lineItemDiscountPercentSchema,
  lineItemDiscountFixedSchema,
]);

export const lineItemDiscountInputSchema: z.ZodType<LineItemDiscountInput> =
  lineItemDiscountInputSchema_;
export type LineItemDiscountInput = z.infer<
  typeof lineItemDiscountInputSchema_
>;
